/**
 * URL path segment asosidagi super app hostlar uchun factory.
 *
 * Ba'zi super app'lar (mas. Paynet) mini app'ni imzolangan tokenni URL query
 * emas, URL YO'LIGA (path) joylab ochadi:
 *
 *     https://paynet.insonuchun.uz/paynet/login/<token>
 *
 * Bu factory shu pattern'ni yagonalashtiradi: `pathPrefix` dan keyingi segment
 * token sifatida olinadi, backenddagi `/MiniApp/<Vendor>Login` ga
 * `{ [payloadKey]: token }` bo'lib jo'natiladi, so'ng token URL'dan tozalanadi —
 * reload/ulashishda qayta ishlatilib qolmasin.
 *
 * `defineQueryParamHost` bilan bir xil hayotiy davr (reload'da tokenni qayta
 * ishlatish, sessiya eskirsa xato) — yagona farq: signal manbai `?param` emas,
 * path segment.
 *
 * Yangi vendor qo'shish — bu faylga teginmasdan, faqat `definePathParamHost({...})`
 * ni chaqirib `src/hosts/index.ts` ga ro'yxatga olish kifoya.
 */

import { apiPost, apiPostBeacon } from "@/api/client";
import { setToken, clearToken, getToken, isTokenValid } from "@/lib/auth";
import type { BrandId } from "@/config/uiTokens";
import type { HostAdapter, HostId, LoginResponse, VendorLoginResponse } from "./types";
import { mapVendorResponse } from "./types";

export interface PathParamHostConfig {
  id: HostId;
  brandId: BrandId;
  /** Super app domeni (mas. "paynet.insonuchun.uz") */
  hostnames: readonly string[];
  /** Backenddagi login endpoint (mas. "/MiniApp/PaynetLogin") */
  loginEndpoint: string;
  /**
   * URL path prefiksi — token shundan keyingi birinchi segment.
   * Mas. `"/paynet/login/"` → `"/paynet/login/<token>"` dan `"<token>"` olinadi.
   */
  pathPrefix: string;
  /** Backendga jo'natiladigan JSON payload kaliti (mas. "token") */
  payloadKey: string;
  /**
   * Ixtiyoriy logout endpoint. Ko'rsatilsa, `pagehide` da fire-and-forget
   * POST beacon jo'natiladi (token har holda tozalanadi).
   */
  logoutEndpoint?: string;
  /**
   * Login javobi kelgach (token saqlangach) chaqiriladi — vendor-specific yon
   * effektlar uchun (mas. javobdagi `theme` ni saqlash). Factory bu yerda
   * mavzu/til detallarini BILMAYDI; qaror vendor konfigida qabul qilinadi.
   */
  onLoginResponse?(raw: VendorLoginResponse): void;
}

/** `pathPrefix` dan keyingi birinchi path segmentini (token) ajratib beradi */
function extractPathToken(pathname: string, prefix: string): string | null {
  if (!pathname.startsWith(prefix)) return null;
  const segment = pathname.slice(prefix.length).split("/")[0];
  if (!segment) return null;
  // Path segmenti odatda base64url token — URL-encoding bo'lmasa ham xavfsiz
  // dekod qilamiz; buzuq `%` ketma-ketligida xom qiymatni qaytaramiz.
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
}

/**
 * `localhost`/`127.0.0.1` bir nechta narsa uchun bir vaqtda ishlatiladi: `webHost`ning
 * oddiy dev fallback'i (Click flow, pathga bog'liq emas) VA har xil path-param
 * vendorlarning o'z `pathPrefix`ini local test qilish qulayligi. Agar bu hostlarda
 * ham boshqa domenlar kabi faqat hostname bo'yicha sync match qilinsa, registryda
 * birinchi turgan path-param host (mas. Paynet) HAR QANDAY localhost so'rovini
 * (path'idan qat'i nazar) o'ziga oladi — shu jumladan oddiy `localhost:5898`
 * dev root'ini ham — va webHost'ning Click fallback'iga hech qachon yetib
 * bo'lmaydi ("session expired" xatosi bilan to'xtaydi).
 */
const DEV_AMBIGUOUS_HOSTNAMES = new Set(["localhost", "127.0.0.1"]);

export function definePathParamHost(config: PathParamHostConfig): HostAdapter {
  const hostnameSet = new Set(config.hostnames);

  return {
    id: config.id,
    brandId: config.brandId,

    match() {
      if (typeof window === "undefined") return false;
      const hostname = window.location.hostname;
      if (!hostnameSet.has(hostname)) return false;
      // Ambiguous dev hostda faqat shu vendorning pathPrefix'i bo'lsagina claim
      // qilamiz — aks holda bare `localhost:5898` webHost'ga tushishi kerak.
      if (DEV_AMBIGUOUS_HOSTNAMES.has(hostname)) {
        return window.location.pathname.startsWith(config.pathPrefix);
      }
      return true;
    },

    async login(): Promise<LoginResponse> {
      const token = extractPathToken(window.location.pathname, config.pathPrefix);

      // Path'da token yo'q = reload (token allaqachon URL'dan tozalangan).
      // Mavjud sessionStorage token hali yaroqli bo'lsa — uni qayta ishlatamiz.
      if (!token) {
        const existing = getToken();
        if (isTokenValid(existing)) {
          return { token: existing!, phoneNumber: "", fullName: "" };
        }
        // Token yo'q/eskirgan va path'da ham token yo'q — login imkonsiz.
        throw new Error(`${config.id}: session expired. Please reopen the app.`);
      }

      // Path'da token bor = yangi sessiya: eski tokenni tozalab yangisini olamiz.
      clearToken();

      const raw = await apiPost<VendorLoginResponse>(config.loginEndpoint, {
        [config.payloadKey]: token,
      });
      setToken(raw.token);
      config.onLoginResponse?.(raw);

      // Muvaffaqiyatli login — tokenni URL path'idan olib tashlaymiz (reload yo'q).
      // Query/hash saqlanadi (?lang/?theme kabi launch paramlar App'da tozalanadi).
      const cleanUrl = `/${window.location.search}${window.location.hash}`;
      window.history.replaceState(null, "", cleanUrl);

      return mapVendorResponse(raw);
    },

    logout() {
      // App yopilganda sessionStorage'ni tozalaymiz — keyingi kirishda path'dagi
      // yangi token bilan qayta login bo'lishi uchun.
      clearToken();
      if (config.logoutEndpoint) {
        apiPostBeacon(config.logoutEndpoint);
      }
    },
  };
}
